'use client';

import { c as _c } from "react/compiler-runtime";
import type { InternalHref } from '@/utils/app-paths';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { platform } from '@todesktop/client-core';
import { Copy, Download, ExternalLink, Home } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { toast } from 'sonner';
import { useLocalStorage } from 'usehooks-ts';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Logo } from '@/components/icons';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu';
import { buildDesktopAppLink } from '@/lib/desktop-handoff/open-in-app';
import { cn } from '@/lib/utils';
type LogoWithContextMenuProps = {
  showText?: boolean;
  href?: InternalHref;
  className?: string;
};
const svg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0" width="16" height="16" rx="2" fill="#FFFFFF" /></svg>`;
export function LogoWithContextMenu(t0) {
  const $ = _c(37);
  const {
    showText: t1,
    href: t2,
    className
  } = t0;
  const showText = t1 === undefined ? true : t1;
  const href = t2 === undefined ? "/" : t2;
  const pathname = usePathname();
  const [inboxTipShownCount, setInboxTipShownCount] = useLocalStorage("inboxTipShownCount", 0);
  let t3;
  if ($[0] !== inboxTipShownCount || $[1] !== setInboxTipShownCount) {
    t3 = () => {
      if (inboxTipShownCount < 1) {
        toast.info("Pro tip: use the logo to quickly navigate to the inbox", {
          duration: 6000
        });
        setInboxTipShownCount(inboxTipShownCount + 1);
      }
    };
    $[0] = inboxTipShownCount;
    $[1] = setInboxTipShownCount;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleLogoClick = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Logo size={16} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== showText) {
    t5 = showText && <span className="text-xl font-medium text-white">cubic</span>;
    $[4] = showText;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <div className="flex items-center gap-2">{t4}{t5}</div>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const logoContent = t6;
  const t7 = showText ? "flex items-center gap-2 px-3 py-1.5 -mx-3 [&>svg]:-mb-0.5" : "flex shrink-0 items-center justify-center p-2";
  let t8;
  if ($[8] !== className || $[9] !== t7) {
    t8 = cn("logo-with-context-menu rounded-md transition-colors hover:bg-zinc-900", t7, className);
    $[8] = className;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const triggerClassName = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ContextMenuItem onSelect={_temp}><Copy className="mr-2 size-4 text-zinc-400" />Copy logo as SVG</ContextMenuItem>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ContextMenuItem onSelect={_temp2}><Download className="mr-2 size-4 text-zinc-400" />Download logo as SVG</ContextMenuItem>;
    t11 = !platform.todesktop.isDesktopApp() && <ContextMenuItem onSelect={_temp3}><ExternalLink className="mr-2 size-4 text-zinc-400" />Open in desktop app</ContextMenuItem>;
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  let t12;
  if ($[14] !== pathname) {
    t12 = pathname !== "/" && !platform.todesktop.isDesktopApp() && <><Separator className="my-1" /><ContextMenuItem asChild={true}><Link href="/" className="flex items-center" prefetch={true}><Home className="mr-2 size-4 text-zinc-400" />Go to home page</Link></ContextMenuItem></>;
    $[14] = pathname;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12) {
    t13 = <ContextMenuContent>{t9}{t10}{t11}{t12}</ContextMenuContent>;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const contextMenuContent = t13;
  if (href) {
    let t14;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = {
        WebkitAppRegion: "no-drag"
      };
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    let t15;
    if ($[19] !== handleLogoClick || $[20] !== href || $[21] !== logoContent || $[22] !== triggerClassName) {
      t15 = <TooltipTrigger asChild={true}><ContextMenuTrigger asChild={true}><Link href={href} className={triggerClassName} prefetch={true} onClick={handleLogoClick} style={t14}>{logoContent}</Link></ContextMenuTrigger></TooltipTrigger>;
      $[19] = handleLogoClick;
      $[20] = href;
      $[21] = logoContent;
      $[22] = triggerClassName;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    let t16;
    if ($[24] !== contextMenuContent || $[25] !== t15) {
      t16 = <ContextMenu>{t15}{contextMenuContent}</ContextMenu>;
      $[24] = contextMenuContent;
      $[25] = t15;
      $[26] = t16;
    } else {
      t16 = $[26];
    }
    let t17;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <TooltipContent side="right">Go to Pull requests</TooltipContent>;
      $[27] = t17;
    } else {
      t17 = $[27];
    }
    let t18;
    if ($[28] !== t16) {
      t18 = <TooltipProvider><Tooltip delayDuration={200}>{t16}{t17}</Tooltip></TooltipProvider>;
      $[28] = t16;
      $[29] = t18;
    } else {
      t18 = $[29];
    }
    return t18;
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      WebkitAppRegion: "no-drag"
    };
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== logoContent || $[32] !== triggerClassName) {
    t15 = <ContextMenuTrigger asChild={true}><div className={triggerClassName} style={t14}>{logoContent}</div></ContextMenuTrigger>;
    $[31] = logoContent;
    $[32] = triggerClassName;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== contextMenuContent || $[35] !== t15) {
    t16 = <ContextMenu>{t15}{contextMenuContent}</ContextMenu>;
    $[34] = contextMenuContent;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}
function _temp3() {
  window.location.href = buildDesktopAppLink(window.location.pathname);
}
function _temp2() {
  const blob = new Blob([svg], {
    type: "image/svg+xml"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "cubic-logo.svg";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function _temp() {
  navigator.clipboard.writeText(svg);
}